import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import { createRecurringReceivable, newNotification } from 'actions';
import { Navigation } from 'components/Navigation/Navigation';
import { PaymentStep } from 'components/PaymentStep/PaymentStep';
import { useWizard } from 'components/Wizard/components/context';
import {
  RECIPIENT_FIELD,
  SENDER_COUNTRY_FIELD,
  STEP_RECURRING_CARD_PAYMENT,
} from 'constants/index';
import type { RootState } from 'reducers/types';
import { getAccessToken, getFieldValues } from 'selectors';
import { useTranslations } from 'utils';

import { Payment } from './components/Payment/Payment';
import {
  CardPaymentProvider,
  useCardPaymentContext,
} from './components/Payment/hooks/useCardPaymentContext';
import { Steps } from './components/Payment/hooks/useSteps';

type CardPaymentProps = ConnectedProps<typeof connector>;

const EMPTY_STRING = '';

function CardPaymentWizard() {
  const { currentStep } = useCardPaymentContext();
  const i18n = useTranslations();
  const { goToPrevStep } = useWizard();
  const navigation = (
    <Navigation
      hideNext
      onClickPrev={goToPrevStep}
      nextStepName={i18n.t('stepsList.track') as string}
    />
  );

  return (
    <PaymentStep
      navigation={navigation}
      stepName={STEP_RECURRING_CARD_PAYMENT}
      title={
        [Steps.Success, Steps.Error].includes(currentStep.id)
          ? EMPTY_STRING
          : (i18n.t('stepsList.pay') as string)
      }
      hasForm={false}
    >
      <Payment />
    </PaymentStep>
  );
}

export function CardPaymentComponent({
  accessToken,
  country,
  recipientId,
  createRecurringReceivable,
  newNotification,
}: CardPaymentProps) {
  const { goToNextStep, goToPrevStep, goToStep } = useWizard();

  return (
    <CardPaymentProvider
      accessToken={accessToken as string}
      createRecurringReceivable={createRecurringReceivable}
      country={country as string}
      newNotification={newNotification}
      recipientId={recipientId as string}
      paymentWizardNavigation={{
        next: goToNextStep,
        prev: goToPrevStep,
        // @ts-expect-error this requires modify useWizard
        go: goToStep,
      }}
    >
      <CardPaymentWizard />
    </CardPaymentProvider>
  );
}

const mapStateToProps = (state: RootState) => {
  const { [SENDER_COUNTRY_FIELD]: country, [RECIPIENT_FIELD]: recipientId } =
    getFieldValues(state);
  const accessToken = getAccessToken(state);

  return { accessToken, country, recipientId };
};

const mapDispatchToProps = {
  createRecurringReceivable,
  newNotification,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const CardPayment = connector(CardPaymentComponent);

export { CardPayment };
